import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  Book,
  ReaderSettings,
  Highlight,
  Note,
  Bookmark,
  ViewType,
  LibraryView,
  Theme,
  FontFamily,
} from '../types';

// 📚 통합 목차 아이템 타입 (EPUB + PDF 공용)
export interface TocItem {
  id: string;
  label: string;
  href: string;        // EPUB용
  pageNumber?: number; // PDF용
  subitems?: TocItem[];
}

interface AppState {
  // View State
  currentView: ViewType;
  libraryView: LibraryView;
  setCurrentView: (view: ViewType) => void;
  setLibraryView: (view: LibraryView) => void;

  // Books
  books: Book[];
  currentBook: Book | null;
  addBook: (book: Book) => void;
  removeBook: (id: string) => void;
  updateBook: (id: string, updates: Partial<Book>) => void;
  setCurrentBook: (book: Book | null) => void;

  // Reader Settings
  readerSettings: ReaderSettings;
  updateReaderSettings: (settings: Partial<ReaderSettings>) => void;
  setTheme: (theme: Theme) => void;
  setFontSize: (size: number) => void;
  setLineHeight: (height: number) => void;
  setFontFamily: (family: FontFamily) => void;
  setBrightness: (brightness: number) => void;

  // Highlights & Notes
  highlights: Highlight[];
  notes: Note[];
  addHighlight: (highlight: Highlight) => void;
  removeHighlight: (id: string) => void;
  addNote: (note: Note) => void;
  updateNote: (id: string, content: string) => void;
  removeNote: (id: string) => void;
  getBookHighlights: (bookId: string) => Highlight[];
  getBookNotes: (bookId: string) => Note[];

  // 📚 북마크 - 홍익인간 (Hongik Ingan)
  bookmarks: Bookmark[];
  addBookmark: (bookmark: Bookmark) => void;
  removeBookmark: (id: string) => void;
  getBookBookmarks: (bookId: string) => Bookmark[];
  isPageBookmarked: (bookId: string, pageNumber?: number, cfi?: string) => boolean;

  // UI State
  showSettings: boolean;
  showTOC: boolean;
  showAIMenu: boolean;
  selectedText: string | null;
  toggleSettings: () => void;
  toggleTOC: () => void;
  toggleAIMenu: () => void;
  setSelectedText: (text: string | null) => void;

  // 📚 통합 목차 (EPUB + PDF 공용)
  tocItems: TocItem[];
  tocType: 'epub' | 'pdf' | null;
  setTocItems: (items: TocItem[], type: 'epub' | 'pdf') => void;
  clearToc: () => void;
}

const defaultReaderSettings: ReaderSettings = {
  fontSize: 18,
  lineHeight: 1.6,
  fontFamily: 'nanum-myeongjo',
  theme: 'light',
  brightness: 100,
  preserveOriginalStyle: false,
};

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      // View State
      currentView: 'library',
      libraryView: 'grid',
      setCurrentView: (view) => set({ currentView: view }),
      setLibraryView: (view) => set({ libraryView: view }),

      // Books
      books: [],
      currentBook: null,
      addBook: (book) =>
        set((state) => ({
          books: [...state.books, book],
        })),
      removeBook: (id) =>
        set((state) => ({
          books: state.books.filter((b) => b.id !== id),
        })),
      updateBook: (id, updates) =>
        set((state) => ({
          books: state.books.map((b) => (b.id === id ? { ...b, ...updates } : b)),
        })),
      setCurrentBook: (book) => set({ currentBook: book }),

      // Reader Settings
      readerSettings: defaultReaderSettings,
      updateReaderSettings: (settings) =>
        set((state) => ({
          readerSettings: { ...state.readerSettings, ...settings },
        })),
      setTheme: (theme) =>
        set((state) => ({
          readerSettings: { ...state.readerSettings, theme },
        })),
      setFontSize: (fontSize) =>
        set((state) => ({
          readerSettings: { ...state.readerSettings, fontSize },
        })),
      setLineHeight: (lineHeight) =>
        set((state) => ({
          readerSettings: { ...state.readerSettings, lineHeight },
        })),
      setFontFamily: (fontFamily) =>
        set((state) => ({
          readerSettings: { ...state.readerSettings, fontFamily },
        })),
      setBrightness: (brightness) =>
        set((state) => ({
          readerSettings: { ...state.readerSettings, brightness },
        })),

      // Highlights & Notes
      highlights: [],
      notes: [],
      addHighlight: (highlight) =>
        set((state) => ({
          highlights: [...state.highlights, highlight],
        })),
      removeHighlight: (id) =>
        set((state) => ({
          highlights: state.highlights.filter((h) => h.id !== id),
        })),
      addNote: (note) =>
        set((state) => ({
          notes: [...state.notes, note],
        })),
      updateNote: (id, content) =>
        set((state) => ({
          notes: state.notes.map((n) =>
            n.id === id ? { ...n, content, updatedAt: new Date() } : n
          ),
        })),
      removeNote: (id) =>
        set((state) => ({
          notes: state.notes.filter((n) => n.id !== id),
        })),
      getBookHighlights: (bookId) => {
        return get().highlights.filter((h) => h.bookId === bookId);
      },
      getBookNotes: (bookId) => {
        return get().notes.filter((n) => n.bookId === bookId);
      },

      // 📚 북마크 - 홍익인간 (Hongik Ingan)
      bookmarks: [],
      addBookmark: (bookmark) =>
        set((state) => ({
          bookmarks: [...state.bookmarks, bookmark],
        })),
      removeBookmark: (id) =>
        set((state) => ({
          bookmarks: state.bookmarks.filter((b) => b.id !== id),
        })),
      getBookBookmarks: (bookId) => {
        return get().bookmarks.filter((b) => b.bookId === bookId);
      },
      isPageBookmarked: (bookId, pageNumber, cfi) => {
        const bookmarks = get().bookmarks.filter((b) => b.bookId === bookId);
        if (pageNumber !== undefined) {
          return bookmarks.some((b) => b.pageNumber === pageNumber);
        }
        if (cfi) {
          return bookmarks.some((b) => b.cfi === cfi);
        }
        return false;
      },

      // UI State
      showSettings: false,
      showTOC: false,
      showAIMenu: false,
      selectedText: null,
      toggleSettings: () => set((state) => ({ showSettings: !state.showSettings })),
      toggleTOC: () => set((state) => ({ showTOC: !state.showTOC })),
      toggleAIMenu: () => set((state) => ({ showAIMenu: !state.showAIMenu })),
      setSelectedText: (text) => set({ selectedText: text }),

      // 📚 통합 목차
      tocItems: [],
      tocType: null,
      setTocItems: (items, type) => set({ tocItems: items, tocType: type }),
      clearToc: () => set({ tocItems: [], tocType: null }),
    }),
    {
      name: 'wia-books-storage',
      partialize: (state) => ({
        books: state.books,
        readerSettings: state.readerSettings,
        highlights: state.highlights,
        notes: state.notes,
        bookmarks: state.bookmarks,
        libraryView: state.libraryView,
      }),
    }
  )
);

// 하위 호환성을 위한 별칭
export type EPUBTocItem = TocItem;
